import { useId, type ReactNode } from 'react'
import { Button } from '@/custolink-newdesign/ui/button'

/**
 * Modal overlay for the remote-support room shell, blocking interaction with the call until the
 * user picks an action. Semantics mirror BlockingMessage in the old room
 * (custolink-views/shared/VideoRoomPage.tsx): a title, free-form body, an optional non-destructive
 * secondary action ("Keep the call") and an optional primary action ("End anyway").
 */
export function RoomBlockingCard(p: {
  title: string
  children: ReactNode
  actionLabel?: string
  onAction?: () => void
  actionDisabled?: boolean
  /** A non-destructive way out, e.g. "Keep the call" next to "End anyway". */
  secondaryLabel?: string
  onSecondary?: () => void
}) {
  const titleId = useId()

  return (
    <div className="cl:absolute cl:inset-0 cl:z-30 cl:flex cl:items-center cl:justify-center cl:bg-black/60 cl:p-4">
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        role="alertdialog"
        className="cl:bg-background cl:text-foreground cl:rounded-xl cl:p-5 cl:max-w-sm cl:w-full cl:shadow-lg"
      >
        <h4 id={titleId} className="cl:font-semibold cl:mb-2">
          {p.title}
        </h4>
        <div className="cl:text-sm cl:text-muted-foreground cl:mb-4">{p.children}</div>

        {(p.actionLabel != null || p.secondaryLabel != null) && (
          <div className="cl:flex cl:justify-end cl:gap-2">
            {p.secondaryLabel != null && p.onSecondary != null && (
              <Button
                variant="outline"
                disabled={p.actionDisabled}
                className="cl:min-h-[44px]"
                onClick={p.onSecondary}
              >
                {p.secondaryLabel}
              </Button>
            )}
            {p.actionLabel != null && p.onAction != null && (
              <Button
                variant="default"
                disabled={p.actionDisabled}
                className="cl:min-h-[44px]"
                onClick={p.onAction}
              >
                {p.actionLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
